import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import dealerService from "../services/dealerService";

const CreateDealerModal = ({ show, onClose, onDealerCreated }) => {
  const [formData, setFormData] = useState({
    full_name: "",
    username: "",
    password: "",
    mobile: ""
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (show) {
      document.body.classList.add("modal-open");
      const originalOverflow = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => {
        document.body.classList.remove("modal-open");
        document.body.style.overflow = originalOverflow;
      };
    }
  }, [show]);

  if (!show) return null;

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "mobile") {
      const digitsOnly = value.replace(/\D/g, "").slice(0, 10);
      setFormData((prev) => ({ ...prev, mobile: digitsOnly }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.full_name || !formData.username || !formData.password) {
      toast.error("Please fill in all required fields!");
      return;
    }

    const cleanMobile = formData.mobile.trim();
    if (cleanMobile && (cleanMobile.length !== 10 || !/^[6-9]\d{9}$/.test(cleanMobile))) {
      toast.error("Please enter a valid 10-digit mobile number (starting with 6, 7, 8, or 9)!");
      return;
    }

    try {
      setLoading(true);
      const res = await dealerService.createDealer(formData);
      if (res && res.success) {
        toast.success(res.message || "Dealer created successfully!");
        setFormData({ full_name: "", username: "", password: "", mobile: "" });
        onDealerCreated();
        onClose();
      } else {
        toast.error(res.message || "Failed to create dealer.");
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Error creating dealer.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div
        className="modal-backdrop fade show"
        style={{ zIndex: 1050 }}
        onClick={onClose}
      />
      <div
        className="modal fade show d-block"
        tabIndex="-1"
        role="dialog"
        aria-modal="true"
        style={{ zIndex: 1055, overflow: "hidden" }}
        onMouseDown={(event) => {
          if (event.target === event.currentTarget) {
            onClose();
          }
        }}
      >
        <div
          className="modal-dialog modal-dialog-centered my-auto mx-auto px-2 px-sm-3"
          style={{ maxWidth: "480px" }}
        >
          <div className="modal-content border border-[rgba(148,163,184,0.2)] bg-[#121827] shadow-2xl rounded-2xl overflow-hidden text-[#F8FAFC]">
            {/* HEADER */}
            <div className="modal-header bg-[#0D111C] border-b border-[rgba(148,163,184,0.14)] py-3 px-4 d-flex align-items-center justify-content-between">
              <div className="d-flex align-items-center gap-2.5">
                <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-[#D4AF37]/10 text-[#D4AF37] border border-[#D4AF37]/30 text-lg font-bold">
                  <i className="bi bi-person-plus-fill" />
                </div>
                <div>
                  <h5 className="modal-title fw-bold text-[#F8FAFC] fs-6 mb-0">
                    Create New Dealer
                  </h5>
                  <div className="text-[10px] text-[#94A3B8]">
                    Register new dealer account
                  </div>
                </div>
              </div>

              <button
                type="button"
                className="btn-close btn-close-white"
                aria-label="Close"
                onClick={onClose}
                disabled={loading}
              />
            </div>

            <form onSubmit={handleSubmit}>
              <div className="modal-body p-4 space-y-3">
                <div>
                  <label className="text-[11px] font-bold uppercase tracking-wider text-[#94A3B8] mb-1.5 block">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    name="full_name"
                    className="w-full rounded-xl border border-[rgba(148,163,184,0.16)] bg-[#0D111C] px-3.5 py-2.5 text-sm text-[#F8FAFC] placeholder:text-[#64748B] focus:border-[#D4AF37] focus:outline-none"
                    placeholder="Enter full name"
                    value={formData.full_name}
                    onChange={handleChange}
                    disabled={loading}
                    required
                  />
                </div>

                <div>
                  <label className="text-[11px] font-bold uppercase tracking-wider text-[#94A3B8] mb-1.5 block">
                    Mobile Number
                  </label>
                  <input
                    type="tel"
                    name="mobile"
                    className="w-full rounded-xl border border-[rgba(148,163,184,0.16)] bg-[#0D111C] px-3.5 py-2.5 text-sm text-[#F8FAFC] placeholder:text-[#64748B] focus:border-[#D4AF37] focus:outline-none"
                    placeholder="Enter 10-digit mobile number"
                    value={formData.mobile}
                    onChange={handleChange}
                    maxLength={10}
                    inputMode="numeric"
                    disabled={loading}
                  />
                </div>

                <div>
                  <label className="text-[11px] font-bold uppercase tracking-wider text-[#94A3B8] mb-1.5 block">
                    Username *
                  </label>
                  <input
                    type="text"
                    name="username"
                    className="w-full rounded-xl border border-[rgba(148,163,184,0.16)] bg-[#0D111C] px-3.5 py-2.5 text-sm text-[#F8FAFC] placeholder:text-[#64748B] focus:border-[#D4AF37] focus:outline-none"
                    placeholder="Enter login username"
                    value={formData.username}
                    onChange={handleChange}
                    disabled={loading}
                    required
                  />
                </div>

                <div>
                  <label className="text-[11px] font-bold uppercase tracking-wider text-[#94A3B8] mb-1.5 block">
                    Password *
                  </label>
                  <input
                    type="password"
                    name="password"
                    className="w-full rounded-xl border border-[rgba(148,163,184,0.16)] bg-[#0D111C] px-3.5 py-2.5 text-sm text-[#F8FAFC] placeholder:text-[#64748B] focus:border-[#D4AF37] focus:outline-none"
                    placeholder="Minimum 6 characters"
                    value={formData.password}
                    onChange={handleChange}
                    disabled={loading}
                    required
                  />
                </div>
              </div>

              <div className="modal-footer bg-[#0D111C] border-t border-[rgba(148,163,184,0.14)] py-3 px-4 d-flex justify-content-end gap-2.5">
                <button
                  type="button"
                  className="rounded-xl border border-[rgba(148,163,184,0.14)] bg-[#121827] px-4 py-2.5 text-xs font-semibold text-[#94A3B8] transition hover:bg-[#182033] hover:text-[#F8FAFC]"
                  onClick={onClose}
                  disabled={loading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="rounded-xl bg-gradient-to-r from-[#D4AF37] to-[#AA7C11] px-5 py-2.5 text-xs font-extrabold text-[#070A12] shadow-md shadow-[#D4AF37]/10 transition hover:opacity-90 disabled:opacity-50"
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-1.5" />
                      Creating...
                    </>
                  ) : (
                    "Create Dealer"
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  );
};

export default CreateDealerModal;
